import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';

import {
  NavLink,
  useNavigate,
  useParams,
} from 'react-router-dom';

import {
  FieldArray,
  Formik,
} from 'formik';

import API from '../../api/axios';

import SmartSearchSelect from '../../components/Smart/SmartSearchSelect';

import useCompanyContext from '../../hooks/useCompanyContext';

const DEFAULT_CURRENCY =
  'EUR';

const getErrorMessage = (
  error,
  fallback,
) => {
  const message =
    error?.response?.data
      ?.message;

  if (
    Array.isArray(
      message,
    )
  ) {
    return message.join(
      ', ',
    );
  }

  return (
    message ||
    error?.message ||
    fallback
  );
};

const normalizeShop = (
  shop,
) => {
  if (!shop) {
    return null;
  }

  const cityName =
    shop.cityName ||
    shop.Location?.City
      ?.name ||
    '';

  const locationName =
    shop.locationName ||
    shop.Location?.name ||
    '';

  const displayName =
    shop.displayName ||
    [
      cityName,
      locationName,
      shop.name,
    ]
      .filter(
        Boolean,
      )
      .join(
        ' / ',
      );

  return {
    ...shop,

    id:
      shop.id,

    name:
      shop.name ||
      '',

    cityName,

    locationName,

    displayName,

    supplierId:
      shop.supplierId ||
      shop.Supplier?.id ||
      '',
  };
};

const normalizeDestination = (
  item,
) => {
  const destinationShop =
    normalizeShop(
      item?.destinationShop,
    );

  const destinationShopId =
    item?.destinationShopId ||
    destinationShop?.id ||
    '';

  return {
    ...item,

    destinationShopId,
    destinationShop,

    isEnabled:
      Boolean(
        item?.isEnabled,
      ),

    priceCents:
      Number(
        item?.priceCents ||
        0,
      ),

    currency:
      item?.currency ||
      DEFAULT_CURRENCY,
  };
};

const unwrapEditResponse = (
  responseData,
) => {
  const body =
    responseData ||
    {};

  if (
    body.data &&
    typeof body.data ===
    'object'
  ) {
    return {
      detail:
        body.data,

      meta:
        body.meta ||
        {},
    };
  }

  return {
    detail:
      body,

    meta:
      body.meta ||
      {},
  };
};

const mergeEditShops = (
  availableShops,
  destinations,
) => {
  const shopMap =
    new Map();

  (
    availableShops ||
    []
  ).forEach(
    (shop) => {
      const normalized =
        normalizeShop(
          shop,
        );

      if (
        normalized?.id
      ) {
        shopMap.set(
          normalized.id,
          normalized,
        );
      }
    },
  );

  (
    destinations ||
    []
  ).forEach(
    (item) => {
      const normalizedDestination =
        normalizeDestination(
          item,
        );

      const shop =
        normalizedDestination.destinationShop;

      if (
        shop?.id &&
        !shopMap.has(
          shop.id,
        )
      ) {
        shopMap.set(
          shop.id,
          shop,
        );
      }
    },
  );

  return Array.from(
    shopMap.values(),
  );
};

const renderOriginSummary = (
  shop,
) => {
  if (!shop) {
    return null;
  }

  return (
    <div className="border rounded p-3 bg-light mt-2">
      <div>
        <strong>
          Name:
        </strong>{' '}
        {shop.name ||
          '-'}
      </div>

      <div>
        <strong>
          City:
        </strong>{' '}
        {shop.cityName ||
          '-'}
      </div>

      <div>
        <strong>
          Location:
        </strong>{' '}
        {shop.locationName ||
          '-'}
      </div>

      {shop.displayName && (
        <div>
          <strong>
            Display:
          </strong>{' '}
          {
            shop.displayName
          }
        </div>
      )}
    </div>
  );
};

const DropoffForm = () => {
  const {
    id:
    routeOriginShopId,
  } = useParams();

  const navigate =
    useNavigate();

  const {
    currentCompanyID,
    buildCompanyUrl,
  } = useCompanyContext();

  const isEditMode =
    Boolean(
      routeOriginShopId,
    );

  const supplierId =
    Number(
      currentCompanyID,
    ) !== 0
      ? String(
        currentCompanyID,
      )
      : '';

  const [
    metaLoading,
    setMetaLoading,
  ] = useState(false);

  const [
    detailLoading,
    setDetailLoading,
  ] = useState(false);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  const [
    submitError,
    setSubmitError,
  ] = useState('');

  const [
    meta,
    setMeta,
  ] = useState({
    supplierId: '',
    shops: [],
  });

  const [
    detail,
    setDetail,
  ] = useState(null);

  const [
    destinationKeyword,
    setDestinationKeyword,
  ] = useState('');

  const [
    bulkPrice,
    setBulkPrice,
  ] = useState('');

  const loadCreateMeta =
    useCallback(
      async () => {
        if (
          !supplierId
        ) {
          throw new Error(
            'Please select a specific company first.',
          );
        }

        const response =
          await API.get(
            '/shop-dropoff-config/meta',
            {
              params: {
                supplierId,
              },
            },
          );

        const responseMeta =
          response?.data ||
          {};

        const shops =
          Array.isArray(
            responseMeta.shops,
          )
            ? responseMeta.shops
              .map(
                normalizeShop,
              )
              .filter(
                Boolean,
              )
            : [];

        return {
          supplierId:
            responseMeta.supplierId ||
            supplierId,

          shops,
        };
      },
      [supplierId],
    );

  const loadEditDetail =
    useCallback(
      async () => {
        const response =
          await API.get(
            `/shop-dropoff-config/${routeOriginShopId}`,
          );

        const {
          detail:
          rawDetail,
          meta:
          responseMeta,
        } =
          unwrapEditResponse(
            response?.data,
          );

        const normalizedDestinations =
          Array.isArray(
            rawDetail
              ?.destinations,
          )
            ? rawDetail.destinations.map(
              normalizeDestination,
            )
            : [];

        const normalizedOriginShop =
          normalizeShop(
            rawDetail
              ?.originShop,
          );

        const availableShops =
          mergeEditShops(
            responseMeta
              ?.availableShops,
            normalizedDestinations,
          );

        const resolvedSupplierId =
          rawDetail
            ?.supplierId ||
          rawDetail
            ?.companyId ||
          responseMeta
            ?.supplierId ||
          responseMeta
            ?.companyId ||
          normalizedOriginShop
            ?.supplierId ||
          '';

        return {
          detail: {
            ...rawDetail,

            supplierId:
              resolvedSupplierId,

            companyId:
              rawDetail
                ?.companyId ||
              resolvedSupplierId,

            originShopId:
              rawDetail
                ?.originShopId ||
              normalizedOriginShop
                ?.id ||
              routeOriginShopId,

            originShop:
              normalizedOriginShop,

            destinations:
              normalizedDestinations,
          },

          meta: {
            supplierId:
              resolvedSupplierId,

            shops:
              availableShops,
          },
        };
      },
      [
        routeOriginShopId,
      ],
    );

  useEffect(() => {
    if (
      isEditMode ||
      !supplierId
    ) {
      return;
    }

    let cancelled =
      false;

    const load =
      async () => {
        try {
          setMetaLoading(
            true,
          );

          setError('');

          const result =
            await loadCreateMeta();

          if (
            cancelled
          ) {
            return;
          }

          setMeta(
            result,
          );

          setDetail(
            null,
          );
        } catch (e) {
          if (
            !cancelled
          ) {
            setError(
              getErrorMessage(
                e,
                'Failed to load shops meta',
              ),
            );
          }
        } finally {
          if (
            !cancelled
          ) {
            setMetaLoading(
              false,
            );
          }
        }
      };

    load();

    return () => {
      cancelled =
        true;
    };
  }, [
    isEditMode,
    supplierId,
    loadCreateMeta,
  ]);

  useEffect(() => {
    if (
      !isEditMode ||
      !routeOriginShopId
    ) {
      return;
    }

    let cancelled =
      false;

    const load =
      async () => {
        try {
          setDetailLoading(
            true,
          );

          setError('');

          const result =
            await loadEditDetail();

          if (
            cancelled
          ) {
            return;
          }

          setDetail(
            result.detail,
          );

          setMeta(
            result.meta,
          );
        } catch (e) {
          if (
            !cancelled
          ) {
            setError(
              getErrorMessage(
                e,
                'Failed to load dropoff detail',
              ),
            );
          }
        } finally {
          if (
            !cancelled
          ) {
            setDetailLoading(
              false,
            );
          }
        }
      };

    load();

    return () => {
      cancelled =
        true;
    };
  }, [
    isEditMode,
    routeOriginShopId,
    loadEditDetail,
  ]);

  const metaShops =
    useMemo(
      () =>
        Array.isArray(
          meta?.shops,
        )
          ? meta.shops
          : [],
      [meta?.shops],
    );

  const originOptions =
    useMemo(
      () =>
        metaShops.map(
          (shop) => {
            const isCurrentEditOrigin =
              isEditMode &&
              routeOriginShopId ===
              shop.id;

            const isDisabled =
              !isCurrentEditOrigin &&
              Boolean(
                shop.hasDropoffConfig,
              );

            return {
              value:
                shop.id,

              label:
                shop.displayName ||
                `${shop.cityName || '-'} / ${shop.locationName || '-'} - ${shop.name}`,

              name:
                shop.name,

              city:
                shop.cityName,

              location:
                shop.locationName,

              hasDropoffConfig:
                Boolean(
                  shop.hasDropoffConfig,
                ),

              isDisabled,

              disabledReason:
                isDisabled
                  ? 'Already configured'
                  : null,

              raw:
                shop,
            };
          },
        ),
      [
        metaShops,
        isEditMode,
        routeOriginShopId,
      ],
    );

  const initialValues =
    useMemo(() => {
      const originId =
        isEditMode
          ? detail
            ?.originShopId ||
          routeOriginShopId ||
          ''
          : '';

      const detailMap =
        new Map(
          (
            detail
              ?.destinations ||
            []
          )
            .map(
              normalizeDestination,
            )
            .filter(
              (item) =>
                item.destinationShopId,
            )
            .map(
              (item) => [
                item.destinationShopId,
                item,
              ],
            ),
        );

      return {
        originShopId:
          originId,

        destinations:
          metaShops.map(
            (rawShop) => {
              const shop =
                normalizeShop(
                  rawShop,
                );

              const existing =
                detailMap.get(
                  shop.id,
                );

              return {
                destinationShopId:
                  shop.id,

                destinationShopName:
                  shop.name,

                destinationCityName:
                  shop.cityName,

                destinationLocationName:
                  shop.locationName,

                enabled:
                  existing
                    ? Boolean(
                      existing.isEnabled,
                    )
                    : false,

                price:
                  existing
                    ? Number(
                      existing.priceCents ||
                      0,
                    ) /
                    100
                    : '',

                currency:
                  existing
                    ?.currency ||
                  DEFAULT_CURRENCY,
              };
            },
          ),
      };
    }, [
      metaShops,
      detail,
      isEditMode,
      routeOriginShopId,
    ]);

  const handleSubmit =
    useCallback(
      async (
        values,
        {
          setSubmitting,
        },
      ) => {
        try {
          setSaving(
            true,
          );

          setSubmitError(
            '',
          );

          if (
            !values.originShopId
          ) {
            setSubmitError(
              'Please select an origin shop',
            );

            return;
          }

          const selectedDestinations =
            (
              values.destinations ||
              []
            )
              .filter(
                (item) =>
                  item.enabled,
              )
              .map(
                (item) => ({
                  destinationShopId:
                    item.destinationShopId,

                  priceCents:
                    Math.round(
                      Number(
                        item.price ||
                        0,
                      ) *
                      100,
                    ),

                  currency:
                    item.currency ||
                    DEFAULT_CURRENCY,

                  isEnabled:
                    true,
                }),
              );

          if (
            !selectedDestinations.length
          ) {
            setSubmitError(
              'Please select at least one destination shop',
            );

            return;
          }

          const invalidPriceItem =
            selectedDestinations.find(
              (item) =>
                Number.isNaN(
                  item.priceCents,
                ) ||
                item.priceCents <
                0,
            );

          if (
            invalidPriceItem
          ) {
            setSubmitError(
              'Please enter a valid price for all selected destination shops',
            );

            return;
          }

          const payload = {
            originShopId:
              values.originShopId,

            destinations:
              selectedDestinations,
          };

          if (
            isEditMode
          ) {
            await API.patch(
              `/shop-dropoff-config/${routeOriginShopId}`,
              {
                destinations:
                  payload.destinations,
              },
            );
          } else {
            await API.post(
              '/shop-dropoff-config',
              payload,
            );
          }

          navigate(
            buildCompanyUrl(
              '/dropoffs',
            ),
          );
        } catch (e) {
          setSubmitError(
            getErrorMessage(
              e,
              'Save failed',
            ),
          );
        } finally {
          setSaving(
            false,
          );

          setSubmitting(
            false,
          );
        }
      },
      [
        buildCompanyUrl,
        isEditMode,
        navigate,
        routeOriginShopId,
      ],
    );

  const isBusy =
    metaLoading ||
    detailLoading;

  return (
    <Formik
      enableReinitialize
      initialValues={
        initialValues
      }
      onSubmit={
        handleSubmit
      }
    >
      {({
        handleSubmit:
        formikHandleSubmit,

        values,

        setFieldValue,

        isSubmitting,
      }) => {
        const selectedOriginShop =
          metaShops.find(
            (shop) =>
              shop.id ===
              values.originShopId,
          ) ||
          detail?.originShop ||
          null;

        const visibleDestinations =
          (
            values.destinations ||
            []
          ).filter(
            (item) =>
              item.destinationShopId !==
              values.originShopId,
          );

        const keyword =
          destinationKeyword
            .trim()
            .toLowerCase();

        const filteredDestinations =
          visibleDestinations.filter(
            (item) => {
              if (!keyword) {
                return true;
              }

              return [
                item.destinationShopName,
                item.destinationCityName,
                item.destinationLocationName,
              ]
                .filter(
                  Boolean,
                )
                .some(
                  (text) =>
                    String(
                      text,
                    )
                      .toLowerCase()
                      .includes(
                        keyword,
                      ),
                );
            },
          );

        const selectedCount =
          visibleDestinations.filter(
            (item) =>
              item.enabled,
          ).length;

        const applyBulkPrice =
          () => {
            const parsed =
              parseFloat(
                bulkPrice,
              );

            if (
              Number.isNaN(
                parsed,
              ) ||
              parsed < 0
            ) {
              return;
            }

            (
              values.destinations ||
              []
            ).forEach(
              (
                item,
                index,
              ) => {
                if (
                  item.enabled
                ) {
                  setFieldValue(
                    `destinations.${index}.price`,
                    parsed,
                  );
                }
              },
            );
          };

        const toggleAllVisible =
          (checked) => {
            filteredDestinations.forEach(
              (item) => {
                const realIndex =
                  values.destinations.findIndex(
                    (
                      destination,
                    ) =>
                      destination.destinationShopId ===
                      item.destinationShopId,
                  );

                if (
                  realIndex <
                  0
                ) {
                  return;
                }

                setFieldValue(
                  `destinations.${realIndex}.enabled`,
                  checked,
                );

                if (
                  checked &&
                  (
                    values.destinations[
                      realIndex
                    ].price ===
                    '' ||
                    values.destinations[
                      realIndex
                    ].price ==
                    null
                  )
                ) {
                  setFieldValue(
                    `destinations.${realIndex}.price`,
                    0,
                  );
                }
              },
            );
          };

        return (
          <Container
            fluid
            className="d-flex flex-column"
          >
            <Row className="mb-4">
              <Col sm={8}>
                <span className="fw-semibold">
                  {isEditMode
                    ? 'Edit Dropoff Configuration'
                    : 'New Dropoff Configuration'}
                </span>
              </Col>

              <Col
                sm={4}
                className="text-end"
              >
                <span className="fw-semibold">
                  <NavLink
                    to={buildCompanyUrl(
                      '/dashboard',
                    )}
                    className="block-link"
                  >
                    Dashboard
                  </NavLink>

                  {
                    ' - Dropoff Information'
                  }
                </span>
              </Col>
            </Row>

            <Row>
              <Col sm={12}>
                <Card className="h-100 p-2">
                  <Card.Body>
                    {error && (
                      <Alert
                        variant="danger"
                        className="mb-3"
                      >
                        {error}
                      </Alert>
                    )}

                    {submitError && (
                      <Alert
                        variant="danger"
                        className="mb-3"
                      >
                        {
                          submitError
                        }
                      </Alert>
                    )}

                    {isBusy ? (
                      <div className="py-5 text-center">
                        <Spinner animation="border" />

                        <div className="mt-2 text-muted">
                          Loading
                          dropoff
                          information...
                        </div>
                      </div>
                    ) : (
                      <Form
                        onSubmit={
                          formikHandleSubmit
                        }
                      >
                        <Row>
                          <Col sm={12}>
                            <Card className="mb-4 border-0 bg-light">
                              <Card.Body>
                                <Row className="align-items-start">
                                  <Col sm={6}>
                                    <Form.Group className="mb-2">
                                      <Form.Label className="fw-semibold mb-2">
                                        Origin
                                        Shop{' '}
                                        <span className="text-danger">
                                          *
                                        </span>
                                      </Form.Label>

                                      {isEditMode ? (
                                        <>
                                          <Form.Control
                                            value={
                                              selectedOriginShop?.displayName ||
                                              selectedOriginShop?.name ||
                                              ''
                                            }
                                            disabled
                                          />

                                          <div className="text-muted small mt-2">
                                            Origin
                                            shop
                                            cannot
                                            be
                                            changed
                                            in
                                            edit
                                            mode.
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <SmartSearchSelect
                                            currentID={
                                              values.originShopId
                                            }
                                            options={
                                              originOptions
                                            }
                                            onChange={(
                                              selectedShop,
                                            ) => {
                                              if (
                                                !selectedShop
                                              ) {
                                                setFieldValue(
                                                  'originShopId',
                                                  '',
                                                );

                                                return;
                                              }

                                              if (
                                                selectedShop.isDisabled ||
                                                selectedShop.hasDropoffConfig
                                              ) {
                                                setSubmitError(
                                                  'This origin shop already has a dropoff configuration. Please choose another shop or go to edit page.',
                                                );

                                                return;
                                              }

                                              setSubmitError(
                                                '',
                                              );

                                              setFieldValue(
                                                'originShopId',
                                                selectedShop.value,
                                              );

                                              const selfIndex =
                                                (
                                                  values.destinations ||
                                                  []
                                                ).findIndex(
                                                  (
                                                    item,
                                                  ) =>
                                                    item.destinationShopId ===
                                                    selectedShop.value,
                                                );

                                              if (
                                                selfIndex >=
                                                0
                                              ) {
                                                setFieldValue(
                                                  `destinations.${selfIndex}.enabled`,
                                                  false,
                                                );

                                                setFieldValue(
                                                  `destinations.${selfIndex}.price`,
                                                  '',
                                                );
                                              }
                                            }}
                                            placeholder="Select origin shop..."
                                          />

                                          <div className="text-muted small mt-2">
                                            Shops
                                            already
                                            configured
                                            as
                                            origin
                                            are
                                            disabled
                                            in
                                            New
                                            mode.
                                          </div>
                                        </>
                                      )}
                                    </Form.Group>
                                  </Col>

                                  <Col sm={6}>
                                    {selectedOriginShop
                                      ? renderOriginSummary(
                                        selectedOriginShop,
                                      )
                                      : (
                                        <div className="text-muted small mt-4">
                                          Select
                                          an
                                          origin
                                          shop
                                          to
                                          configure
                                          destination
                                          shops.
                                        </div>
                                      )}
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          </Col>

                          {values.originShopId ? (
                            <Col sm={12}>
                              <Card className="border-0">
                                <Card.Body className="px-0">
                                  <Row className="mb-3 align-items-end">
                                    <Col md={4}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-2">
                                          Search
                                          Destination
                                          Shops
                                        </Form.Label>

                                        <Form.Control
                                          type="text"
                                          placeholder="Search by shop / city / location"
                                          value={
                                            destinationKeyword
                                          }
                                          onChange={(
                                            event,
                                          ) =>
                                            setDestinationKeyword(
                                              event.target.value,
                                            )
                                          }
                                        />
                                      </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-2">
                                          Bulk
                                          Price
                                        </Form.Label>

                                        <InputGroup>
                                          <Form.Control
                                            type="number"
                                            value={
                                              bulkPrice
                                            }
                                            onChange={(
                                              event,
                                            ) =>
                                              setBulkPrice(
                                                event.target.value,
                                              )
                                            }
                                            min="0"
                                            step="0.01"
                                            placeholder="0.00"
                                          />

                                          <InputGroup.Text>
                                            €
                                          </InputGroup.Text>
                                        </InputGroup>
                                      </Form.Group>
                                    </Col>

                                    <Col
                                      md={5}
                                      className="d-flex gap-2 flex-wrap"
                                    >
                                      <Button
                                        type="button"
                                        variant="outline-secondary"
                                        onClick={() =>
                                          toggleAllVisible(
                                            true,
                                          )
                                        }
                                      >
                                        Select
                                        Visible
                                      </Button>

                                      <Button
                                        type="button"
                                        variant="outline-secondary"
                                        onClick={() =>
                                          toggleAllVisible(
                                            false,
                                          )
                                        }
                                      >
                                        Unselect
                                        Visible
                                      </Button>

                                      <Button
                                        type="button"
                                        variant="outline-primary"
                                        onClick={
                                          applyBulkPrice
                                        }
                                      >
                                        Apply
                                        Price
                                        to
                                        Selected
                                      </Button>
                                    </Col>
                                  </Row>

                                  <Row className="mb-2">
                                    <Col
                                      sm={12}
                                      className="d-flex justify-content-between align-items-center"
                                    >
                                      <div className="fw-semibold">
                                        Destination
                                        Shops
                                      </div>

                                      <div className="text-muted small">
                                        Selected{' '}
                                        {
                                          selectedCount
                                        }{' '}
                                        /{' '}
                                        {
                                          visibleDestinations.length
                                        }
                                      </div>
                                    </Col>
                                  </Row>

                                  <Table
                                    className="my-1"
                                    striped
                                    bordered
                                    hover
                                    responsive
                                  >
                                    <thead>
                                      <tr>
                                        <th
                                          style={{
                                            width:
                                              '44%',
                                          }}
                                        >
                                          Name
                                        </th>

                                        <th
                                          style={{
                                            width:
                                              '16%',
                                          }}
                                        >
                                          City
                                        </th>

                                        <th
                                          style={{
                                            width:
                                              '16%',
                                          }}
                                        >
                                          Location
                                        </th>

                                        <th
                                          style={{
                                            width:
                                              '24%',
                                          }}
                                        >
                                          Price
                                        </th>
                                      </tr>
                                    </thead>

                                    <tbody>
                                      <FieldArray name="destinations">
                                        {() =>
                                          filteredDestinations.length ? (
                                            filteredDestinations.map(
                                              (
                                                item,
                                              ) => {
                                                const index =
                                                  values.destinations.findIndex(
                                                    (
                                                      destination,
                                                    ) =>
                                                      destination.destinationShopId ===
                                                      item.destinationShopId,
                                                  );

                                                if (
                                                  index <
                                                  0
                                                ) {
                                                  return null;
                                                }

                                                return (
                                                  <tr
                                                    key={
                                                      item.destinationShopId
                                                    }
                                                  >
                                                    <td>
                                                      <Form.Check
                                                        type="checkbox"
                                                        id={`dropoff-${item.destinationShopId}`}
                                                        checked={
                                                          Boolean(
                                                            values.destinations[
                                                              index
                                                            ].enabled,
                                                          )
                                                        }
                                                        onChange={(
                                                          event,
                                                        ) => {
                                                          setFieldValue(
                                                            `destinations.${index}.enabled`,
                                                            event.target.checked,
                                                          );

                                                          if (
                                                            event.target.checked &&
                                                            (
                                                              values.destinations[
                                                                index
                                                              ].price ===
                                                              '' ||
                                                              values.destinations[
                                                                index
                                                              ].price ==
                                                              null
                                                            )
                                                          ) {
                                                            setFieldValue(
                                                              `destinations.${index}.price`,
                                                              0,
                                                            );
                                                          }
                                                        }}
                                                        label={
                                                          <span className="ms-2">
                                                            {
                                                              item.destinationShopName
                                                            }
                                                          </span>
                                                        }
                                                        inline
                                                      />
                                                    </td>

                                                    <td>
                                                      <Badge
                                                        bg="primary"
                                                        style={{
                                                          minWidth:
                                                            '120px',
                                                        }}
                                                      >
                                                        {item.destinationCityName ||
                                                          '-'}
                                                      </Badge>
                                                    </td>

                                                    <td>
                                                      {item.destinationLocationName ||
                                                        '-'}
                                                    </td>

                                                    <td>
                                                      <InputGroup>
                                                        <Form.Control
                                                          type="number"
                                                          value={
                                                            values.destinations[
                                                              index
                                                            ].price
                                                          }
                                                          onChange={(
                                                            event,
                                                          ) => {
                                                            const value =
                                                              event.target.value;

                                                            setFieldValue(
                                                              `destinations.${index}.price`,
                                                              value ===
                                                                ''
                                                                ? ''
                                                                : Number(
                                                                  value,
                                                                ),
                                                            );
                                                          }}
                                                          step="0.01"
                                                          min="0"
                                                          disabled={
                                                            !values.destinations[
                                                              index
                                                            ].enabled
                                                          }
                                                        />

                                                        <InputGroup.Text>
                                                          €
                                                        </InputGroup.Text>
                                                      </InputGroup>
                                                    </td>
                                                  </tr>
                                                );
                                              },
                                            )
                                          ) : (
                                            <tr>
                                              <td colSpan={4}>
                                                <div className="text-center py-4 text-muted">
                                                  No
                                                  destination
                                                  shops
                                                  found
                                                </div>
                                              </td>
                                            </tr>
                                          )
                                        }
                                      </FieldArray>
                                    </tbody>
                                  </Table>
                                </Card.Body>
                              </Card>
                            </Col>
                          ) : (
                            <Col sm={12}>
                              <div className="text-muted small mt-2">
                                Please
                                select
                                an
                                origin
                                shop
                                first.
                              </div>
                            </Col>
                          )}

                          <Col
                            sm={12}
                            className="gap-3 mt-3 text-end"
                          >
                            <Button
                              type="button"
                              variant="outline-secondary"
                              className="me-2"
                              onClick={() =>
                                navigate(
                                  buildCompanyUrl(
                                    '/dropoffs',
                                  ),
                                )
                              }
                              disabled={
                                saving ||
                                isSubmitting
                              }
                            >
                              Cancel
                            </Button>

                            <Button
                              type="submit"
                              className="border ms-1 btn-custom-success"
                              disabled={
                                saving ||
                                isSubmitting ||
                                isBusy ||
                                !values.originShopId
                              }
                            >
                              {saving ||
                                isSubmitting
                                ? 'Saving...'
                                : 'Save'}
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        );
      }}
    </Formik>
  );
};

export default DropoffForm;